import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Search, TriangleAlert } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Img, Input, Price, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch, toCents } from "../../api/org-client.ts";
import { useIdentities, useMe } from "../../api/queries.ts";
import { hoursLeft, type PriceNotice, type PricingProduct, type PricingSku } from "../my-pricing.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 我的定价
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **定价是一张表，不是一叠卡片。**
 *    这一页的动作是"**横着比**：我进多少、我卖多少、我赚多少、加了几个点"，
 *    然后挨个改。手机上四个数只能竖着堆，改一个要点开一个面板；
 *    网页上排成表格，一屏能看十几个规格，**哪个规格加价太低一眼就看出来**。
 *
 * ⚠️ 改价直接在行里改（输入框顶掉"我卖"那一格），不弹窗 ——
 *    改价是连续动作，弹窗要开关十几次。
 *
 * ⚠️ 这里的价按**我自己**的链算，跟前台"访问者看到什么价"是两回事。
 *    出参里没有上级的成本 —— 服务端已经裁剪掉了。
 */

function SkuRow({ sku, orgId }: { sku: PricingSku; orgId: number }) {
  const qc = useQueryClient();
  /**
   * 🔴 **平台有两个价，所以"正在改哪一个"要记下来，不能只记 true/false。**
   *    老板：「陈总的店和平台定价是两回事」——
   *    `shop` = 我店里卖给客户多少，`chain` = 下级按多少进货。
   */
  const [editingKind, setEditingKind] = useState<"chain" | "shop" | null>(null);
  const [draft, setDraft] = useState("");
  const editing = editingKind !== null;

  const done = () => {
    setEditingKind(null);
    void qc.invalidateQueries({ queryKey: ["my-pricing", orgId] });
    /** ⚠️ 前台的商品价是另一个查询 —— 不作废的话去店里一看还是老价，会以为没保存上。 */
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const save = useMutation({
    mutationFn: (price: number) =>
      orgFetch("/pricing/sku", orgId, {
        method: "POST",
        body: JSON.stringify({ skuId: sku.skuId, price, kind: editingKind ?? "chain" }),
      }),
    onSuccess: done,
  });

  /** 清除我定的价 → 这个规格回落到默认值（批量加价率 / 平台价）。 */
  const clear = useMutation({
    mutationFn: (kind: "chain" | "shop") =>
      orgFetch(`/pricing/sku/${sku.skuId}?kind=${kind}`, orgId, { method: "DELETE" }),
    onSuccess: done,
  });

  const specText = Object.values(sku.specs).join(" · ") || "默认";
  /** 🔴 只有平台会带这个字段 —— 带了就说明他有两个价要分开管。 */
  const isPlatform = sku.wholesaleSell !== undefined;

  return (
    <>
      <div className="grid grid-cols-[minmax(0,1fr)_120px_170px_120px_90px_120px] items-center gap-4 border-t border-border px-5 py-3">
        <span className="min-w-0 text-[14px]">
          {specText}
          {sku.custom && (
            <span className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-700">
              单独定价
            </span>
          )}
          {sku.warning && (
            <span className="mt-1 flex items-center gap-1 text-[12px] text-amber-600">
              <TriangleAlert className="size-3" />
              {sku.warning}
            </span>
          )}
        </span>

        <span className="text-right text-[14px] text-muted-foreground">
          <Price cents={sku.cost} />
        </span>

        {/* ⚠️ 输入框顶掉"我卖"那一格 —— 行不会跳动，改完下一个还在原位。 */}
        {editingKind === "shop" || (editingKind === "chain" && !isPlatform) ? (
          <span className="flex items-center gap-1.5">
            <Input
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="h-9 w-[110px] text-right"
            />
            <span className="text-[12px] whitespace-nowrap text-muted-foreground">
              元/{sku.unit}
            </span>
          </span>
        ) : (
          <span className="text-right text-[15px]">
            <Price cents={sku.sell} />
            <span className="ml-1 text-[12px] text-muted-foreground">/{sku.unit}</span>
          </span>
        )}

        <span className="text-right text-[14px] text-brand-600">
          <Price cents={sku.margin} />
        </span>
        <span className="text-right text-[13px] text-muted-foreground tabular-nums">
          {(sku.rate * 100).toFixed(1)}%
        </span>

        <span className="flex justify-end gap-2">
          {editing ? (
            <>
              <Button
                size="sm"
                loading={save.isPending}
                onClick={() => {
                  const cents = toCents(draft);
                  if (cents !== null) save.mutate(cents);
                }}
              >
                保存
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingKind(null)}>
                取消
              </Button>
            </>
          ) : (
            <button
              className="text-[13px] text-brand-600 transition-colors hover:text-brand-700"
              onClick={() => {
                setDraft((sku.sell / 100).toFixed(2));
                setEditingKind("shop");
              }}
            >
              改价
            </button>
          )}
        </span>
      </div>

      {/*
        🔴 **平台的第二个价：给下级的批发价。**
           老板：「陈总的店和平台定价是两回事」。上面那一行是**他店里卖给客户**的价，
           这一条是**下级进货**的价 —— 两个数早先挤在一起，
           他以为在给自己店打折，实际把每个下级的进货价翻了一倍。
           所以单独一条、写清楚改它会连带改谁。
      */}
      {isPlatform && (
        <div className="flex items-center gap-3 border-t border-border bg-secondary/30 px-5 py-2.5 text-[13px]">
          <span className="text-muted-foreground">给下级的批发价</span>
          {editingKind === "chain" ? (
            <span className="flex items-center gap-1.5">
              <Input
                inputMode="decimal"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="h-9 w-[110px] text-right"
              />
              <span className="text-[12px] whitespace-nowrap text-muted-foreground">
                元/{sku.unit}
              </span>
            </span>
          ) : (
            <span className="text-[14px]">
              <Price cents={sku.wholesaleSell!} />
            </span>
          )}
          {sku.wholesaleCustom && (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-700">
              单独定价
            </span>
          )}
          <span className="text-[12px] text-muted-foreground">
            经销商 / 分销商按它进货 —— 改它会连带改掉他们的成本
          </span>
          {editingKind !== "chain" && (
            <button
              className="ml-auto text-[13px] text-brand-600 transition-colors hover:text-brand-700"
              onClick={() => {
                setDraft((sku.wholesaleSell! / 100).toFixed(2));
                setEditingKind("chain");
              }}
            >
              改批发价
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className="flex items-center gap-4 border-t border-border bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
          {/* ⚠️ 低于进货价服务端会挡下来，这里先提示，省得他填完才被拒。 */}
          <span>
            正在改「{editingKind === "shop" ? "我店里卖" : isPlatform ? "给下级的批发价" : "我卖"}」
            ，不能低于你的进货价 <Price cents={sku.cost} />
          </span>
          {(editingKind === "shop" ? sku.custom : (sku.wholesaleCustom ?? sku.custom)) && (
            <button
              className="underline transition-colors hover:text-foreground"
              onClick={() => clear.mutate(editingKind!)}
              disabled={clear.isPending}
            >
              {editingKind === "shop" ? "恢复成按平台价卖" : "恢复成按批量加价率"}
            </button>
          )}
          {save.error && <span className="text-destructive">{save.error.message}</span>}
          {clear.error && <span className="text-destructive">{clear.error.message}</span>}
        </div>
      )}
    </>
  );
}

/**
 * 上游调价提醒。
 *
 * 🔴 **这是定死了售价的商家唯一能发现"我在亏本卖"的地方。**
 *    按加价率卖的会自动跟涨，定死价的不会 —— 上游一涨他就一单亏一单。
 *
 * ⚠️ 放在这一页顶部而不是单独开一页：看到提醒的下一步动作就是改价，
 *    两件事之间不该隔一次跳转。
 */
function PriceNotices({ orgId }: { orgId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["price-notices", orgId],
    queryFn: () => orgFetch<{ items: PriceNotice[]; unread: number }>("/price-notices", orgId),
  });

  const ack = useMutation({
    mutationFn: (ids: number[]) =>
      orgFetch<{ acknowledged: number }>("/price-notices/ack", orgId, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["price-notices", orgId] });
      /** 缓冲期内看到的还是旧进货价，确认后顺手把定价表也刷一下。 */
      qc.invalidateQueries({ queryKey: ["my-pricing"] });
    },
  });

  const pending = (q.data?.items ?? []).filter((n) => !n.acknowledgedAt);
  if (pending.length === 0) return null;

  const losing = pending.filter((n) => n.belowCost);

  return (
    <section className="mb-5 overflow-hidden rounded-xl border border-amber-200 bg-card">
      <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-5 py-3">
        <BellRing className="size-[18px] text-amber-600" />
        <p className="flex-1 text-[15px] font-medium text-amber-900">
          上游调价（{pending.length}）
        </p>
        <button
          className="text-[13px] text-amber-800 underline transition-opacity hover:opacity-70"
          onClick={() => ack.mutate(pending.map((n) => n.id))}
          disabled={ack.isPending}
        >
          全部知道了
        </button>
      </div>

      {/* ⚠️ 倒挂的单独说一句 —— 这是要立刻动手的，不能跟普通涨价混在一起。 */}
      {losing.length > 0 && (
        <p className="border-b border-border bg-red-50 px-5 py-2.5 text-[13px] text-red-700">
          有 <strong>{losing.length}</strong> 个规格<strong>已经卖一单亏一单</strong>
          ，涨价生效前改掉你的售价。
        </p>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_200px_120px_minmax(0,220px)] gap-4 border-b border-border px-5 py-2 text-[13px] text-muted-foreground">
        <span>商品</span>
        <span>进货价变化</span>
        <span>生效</span>
        <span>影响</span>
      </div>

      {pending.map((n) => (
        <div
          key={n.id}
          className="grid grid-cols-[minmax(0,1fr)_200px_120px_minmax(0,220px)] items-center gap-4 border-b border-border px-5 py-3 last:border-b-0"
        >
          <span className="min-w-0 text-[14px]">
            {n.title}
            <span className="ml-2 text-[12px] text-muted-foreground">
              {Object.values(n.specs).join(" · ")}
            </span>
          </span>
          <span className="text-[14px] text-muted-foreground">
            <Price cents={n.oldCost} /> → <Price cents={n.newCost} />
          </span>
          <span className="text-[13px] text-muted-foreground">{hoursLeft(n.effectiveAt)}</span>
          {n.belowCost ? (
            <span className="flex items-center gap-1 text-[13px] text-red-600">
              <TriangleAlert className="size-3.5 shrink-0" />
              生效后每单亏 <Price cents={n.newCost - n.currentPrice} />
            </span>
          ) : (
            <span className="text-[13px] text-muted-foreground">
              生效后还赚 <Price cents={n.currentPrice - n.newCost} />
            </span>
          )}
        </div>
      ))}
    </section>
  );
}

export function DesktopMyPricing() {
  const search = useSearch({ from: "/my-pricing" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  /**
   * 能在这里定价的是**链上的转售方**：平台、经销商、分销商。
   * 🔴 平台也在其中；供应商不在（他定的是供货价，走商品编辑那套）。
   */
  const orgs = (identities?.orgs ?? []).filter(
    (o) =>
      o.status === "active" &&
      (o.type === "platform" || o.type === "dealer" || o.type === "distributor"),
  );
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;
  const org = orgs.find((o) => o.id === orgId);

  const [keyword, setKeyword] = useState("");

  const list = useQuery({
    queryKey: ["my-pricing", orgId, keyword],
    queryFn: () =>
      orgFetch<{ items: PricingProduct[]; nextCursor: string | null }>(
        `/pricing/catalog?limit=50${keyword ? `&keyword=${encodeURIComponent(keyword)}` : ""}`,
        orgId!,
      ),
    enabled: Boolean(orgId),
  });

  /**
   * 🔴 只有平台会带 `wholesaleSell` —— 带了就说明这个身份有**两个价**，
   *    表头那一列得改叫「我店里卖」，不然两个数都叫"我卖"，
   *    正是这个含混让人把批发价当成店铺价改了。
   */
  const platformView = (list.data?.items ?? []).some((p) =>
    p.skus.some((s) => s.wholesaleSell !== undefined),
  );

  if (mePending)
    return (
      <AccountShell title="我的定价">
        <Spinner compact />
      </AccountShell>
    );

  if (!me || orgs.length === 0)
    return (
      <AccountShell title="我的定价">
        <Empty
          title="你名下没有可定价的店"
          hint="定价是商家功能，联系上级商家或平台开通"
          compact
        />
      </AccountShell>
    );

  return (
    <AccountShell title="我的定价">
      {orgs.length > 1 && (
        <div className="mb-4 flex gap-2">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={cn(
                "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
                o.id === orgId
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {orgId && <PriceNotices orgId={orgId} />}

      <div className="mb-5 rounded-xl border border-brand-200 bg-brand-50 px-5 py-4 text-[14px] leading-relaxed text-brand-700">
        <p>
          这是「{org?.name}」<strong>卖给客户</strong>的价。
        </p>
        <p className="mt-1">
          🔴 <strong>卖多少钱由你定</strong> —— 在进货价和指导零售价之间，你说了算。
          批量加价率只是还没单独定价时的默认值；这里改过的规格<strong>不会</strong>
          被批量加价率覆盖。
        </p>
      </div>

      <div className="relative mb-4 max-w-[360px]">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="搜商品名"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="pl-10"
        />
      </div>

      {list.isPending ? (
        <Spinner compact />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="上游还没有可卖的商品" compact />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {/* ⚠️ 表头只画一次，钉在最上面 —— 每个商品都画一遍会把表切碎。 */}
          <div className="grid grid-cols-[minmax(0,1fr)_120px_170px_120px_90px_120px] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
            <span>规格</span>
            <span className="text-right">我进货</span>
            {/*
              ⚠️ 平台身份下这一列是「我店里卖」（客户付的），
                 给下级的批发价在每行下面单独一条 —— 两个数不能都叫"我卖"。
            */}
            <span className="text-right">{platformView ? "我店里卖" : "我卖"}</span>
            <span className="text-right">我赚</span>
            <span className="text-right">加价率</span>
            <span />
          </div>

          {list.data.items.map((p) => (
            <div key={p.productId}>
              <div className="flex items-center gap-3 border-t border-border bg-secondary/20 px-5 py-2.5">
                <Img src={p.imageUrl} alt="" className="size-9 shrink-0 rounded object-cover" />
                <p className="min-w-0 flex-1 truncate text-[14px] font-medium">{p.title}</p>
                <span className="shrink-0 text-[12px] text-muted-foreground">
                  {p.skus.length} 个规格
                </span>
              </div>
              {p.skus.map((s) => (
                <SkuRow key={s.skuId} sku={s} orgId={orgId!} />
              ))}
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
